<template>
  <div class="rep-dealer-locator supply-chain-map">
    <div class="container rep-options-wrapper">
      <div class="legend">
        <span class="reps">
          <SloIcon name="User" />
          <span>{{ 'Reps' | t }}</span>
          <span v-if="supplyChainMapResults" class="count">
            ({{ supplyChainMapResults?.localReps.length }})
          </span>
        </span>
        <span class="dealers">
          <SloIcon name="Tag" />
          <span>{{ 'Local' | t }}</span>
          <span class="hidden-sm hidden-md"> {{ 'Distributors' | t }}</span>
          <span v-if="supplyChainMapResults" class="count">
            ({{ supplyChainMapResults?.localDistributors.length }})
          </span>
        </span>
        <a v-if="countryCode === 'US'" href="/company/support/preferred-online-partners" class="show-online">
          <SloIcon name="ArrowUpRight" />
          <span>{{ 'Preferred Online' | t }}</span>
          <span class="hidden-sm hidden-md"> {{ 'Partners' | t }}</span>
        </a>
      </div>
      <form class="rep-dealer-options" @submit.prevent="handleSearch">
        <div class="form-group search">
          <label for="search" class="visually hidden">
            {{ 'Location' | t }}
          </label>
          <div class="input-group">
            <input
              type="search"
              name="search"
              v-model="search"
              :disabled="loading"
              :placeholder="placeholder"
            >
            <span class="input-group-btn">
              <button id="search-button" class="btn-secondary" type="submit">
                <SloIcon name="MagnifyingGlass" />
              </button>
            </span>
          </div>
        </div>
      </form>
    </div>
    <div class="rep-dealer-viewer">
      <div class="rep-dealer-list-wrapper">
        <div class="legend">
          <span class="reps">
            <SloIcon name="User" />
            <span>{{ 'Reps' | t }}</span>
            <span v-if="supplyChainMapResults" class="count">
              ({{ supplyChainMapResults?.localReps.length }})
            </span>
          </span>
          <span class="dealers">
            <SloIcon name="Tag" />
            <span>{{ 'Local' | t }}</span>
            <span class="hidden-sm hidden-md"> {{ 'Distributors' | t }}</span>
            <span v-if="supplyChainMapResults" class="count">
              ({{ supplyChainMapResults?.localDistributors.length }})
            </span>
          </span>
        </div>
        <ul class="rep-dealer-list">
          <li
            v-if="supplyChainMapError ||
            (
              supplyChainMapResults?.localReps.length === 0
              && supplyChainMapResults?.localDistributors.length === 0
            )"
            class="contact-info"
          >
            <h3>{{ 'North American Sales' | t }}</h3>
            <p>
              P: 847.671.4300 / 800.982.5839<br>
              F: 847.671.4380 / 800.447.8329<br>
              <a href="mailto:customer.service@sloan.com">customer.service@sloan.com</a>
            </p>
            <h3>{{ 'International Sales' | t }}</h3>
            <p>
              P: 847.671.4300 / 800.982.5839<br>
              F: 847.671.6851<br>
              <a href="mailto:globalsupport@sloan.com">globalsupport@sloan.com</a>
            </p>
            <h3>亚洲分公司({{ 'Asia branch' | t }})</h3>
            <p>
              电话(phone number)：0086 571-8168 8197<br>
              <a href="mailto:customer.service@sloanasia.com">customer.service@sloanasia.com</a>
            </p>
          </li>
          <li
            v-for="result in supplyChainMapResults?.localReps"
            @click="locateMarker"
            class="rep-dealer rep"
            :id="result.nid"
          >
            <h3 class="rep-dealer-name">
              <span v-if="supplyChainMapResults?.localReps">
                <SloIcon name="User" />
              </span>
              {{ result.title }}
            </h3>
            <div v-if="result.distance" class="rep-dealer-distance">
              ({{ result.distance }} {{ 'Miles' | t }})
            </div>
            <div class="rep-dealer-address">
              {{ result.address_line1 }}<br>
              <span v-if="result.address_line2">
                {{ result.address_line2 }}<br>
              </span>
              {{ result.city + ', ' + result.state + ' ' + (result.zip ? result.zip : '') }}<br>
              {{ result.phone }}<br>
              <a v-if="result.email" :href="'mailto:' + result.email">{{ result.email }}</a>
            </div>
            <div class="rep-dealer-address">
              <a :href="'https://www.google.com/maps/dir/Current+Location/' + result.address_line1 + '+' + result.city + '+' + result.state + '+' + (result.zip ? result.zip : '')" target="_blank">
                {{ 'Get Directions' | t }}
              </a>
            </div>
          </li>
          <li
            v-for="result in supplyChainMapResults?.localDistributors"
            @click="locateMarker"
            class="rep-dealer dealer"
            :id="result.nid"
          >
            <h3 class="rep-dealer-name">
              <span v-if="supplyChainMapResults?.localDistributors">
                <SloIcon name="Tag" />
              </span>
              {{ result.title }}
            </h3>
            <div v-if="result.distance" class="rep-dealer-distance">
              ({{ result.distance }} {{ 'Miles' | t }})
            </div>
            <div class="rep-dealer-address">
              {{ result.address_line1 }}<br>
              <span v-if="result.address_line2">
                {{ result.address_line2 }}<br>
              </span>
              {{ result.city + ', ' + result.state + ' ' + result.zip }}<br>
              {{ result.phone }}
            </div>
            <div class="rep-dealer-address">
              <a
                :href="'https://www.google.com/maps/dir/Current+Location/' + result.address_line1 + '+' + result.city + '+' + result.state + '+' + (result.zip ? result.zip : '')"
                target="_blank"
              >
                {{ 'Get Directions' | t }}
              </a>
            </div>
          </li>
        </ul>
      </div>
      <div class="rep-dealer-map">
        <form class="rep-dealer-options" @submit.prevent="handleSearch">
          <div class="form-group search">
            <label for="search" class="visually hidden">
              {{ 'Location' | t }}
            </label>
            <div class="input-group">
              <input
                type="search"
                name="search"
                v-model="search"
                :disabled="loading"
                :placeholder="placeholder"
              >
              <span class="input-group-btn">
                <button id="search-button" class="btn-secondary" type="submit">
                  <SloIcon name="MagnifyingGlass" />
                </button>
              </span>
            </div>
          </div>
        </form>
        <div class="where-to-buy-map hidden-xs" id="where-to-buy-map">
          <SloLoader :loading="loading" size="xl" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery';
import t from '../../js/main/i18n';
import SloLoader from './SloLoader.vue';
import SloIcon from './SloIcon.vue';

const pinPath = 'M34.6,9.2c-0.8-1.8-1.8-3.4-3.2-4.8C30,3,28.5,2,26.6,1.2C24.8,0.4,22.9,0,20.8,0c-2.1,0-4,0.4-5.8,1.2 C13.1,2,11.6,3,10.2,4.4C8.8,5.7,7.8,7.3,7,9.2C6.2,11,5.8,12.9,5.8,15c0,1.6,0.2,3.1,0.7,4.6c0.5,1.5,1.2,2.9,2.2,4.2l0,0.1 L19.6,41c0.1,0.2,0.3,0.3,0.5,0.4c0.2,0.1,0.4,0.1,0.6,0.1c0.2,0,0.5,0,0.6-0.1c0.2-0.1,0.4-0.2,0.5-0.4l10.9-17.1l0.1-0.1 c0.9-1.3,1.7-2.7,2.1-4.2c0.5-1.5,0.7-3,0.7-4.6C35.7,12.9,35.4,11,34.6,9.2z';

const repPin = {
  path: pinPath,
  strokeColor: '#006bb6',
  fillColor: '#006bb6',
  fillOpacity: 0.5
};

const repPinActive = {
  path: pinPath,
  strokeColor: '#053f63',
  fillColor: '#006bb6',
  fillOpacity: 1
};

const dealerPin = {
  path: pinPath,
  strokeColor: '#7ac254',
  fillColor: '#7ac254',
  fillOpacity: 0.75
};

const dealerPinActive = {
  path: pinPath,
  strokeColor: '#2f5127',
  fillColor: '#7ac254',
  fillOpacity: 1
};

export default {
  name: 'SloRepMap',
  components: {
    SloLoader,
    SloIcon
  },
  filters: {
    t
  },
  data() {
    return {
      loading: true,
      google: false,
      mapListener: null,
      scrollListener: null,
      search: '',
      placeholder: t('Enter your zip code...'),
      supplyChainMapError: false,
      supplyChainMapLoading: true,
      supplyChainMapResults: {
        localReps: [],
        localDistributors: []
      },
      map: '',
      markers: [],
      lat: '39',
      long: '-95',
      zipCode: '',
      countryCode: 'US'
    };
  },
  created() {
    if (!window.google) {
      const googleMapsApiKey = document.head.querySelector('meta[name="google-maps-api-key"]').content;
      const googleMapsScript = document.createElement('script');
      googleMapsScript.setAttribute(
        'src',
        `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&callback=Function.prototype`
      );
      document.head.appendChild(googleMapsScript);
    }

    setTimeout(() => {
      const google = window.google;
      if (google) {
        this.google = google;
        this.locate();
        this.mapListener = this.google.maps.event.addListener(window, 'load', () => this.initializeMap);
        this.scrollListener = event => event.preventDefault();
        document.addEventListener('scroll', this.scrollListener);
      }
    }, 750);
  },
  destroyed() {
    if (this.mapListener) {
      this.google.maps.event.removeListener(this.mapListener);
    }
    if (this.scrollListener) {
      document.removeEventListener('scroll', this.scrollListener);
    }
  },
  watch: {
    supplyChainMapResults: function() {
      this.initializeMap();
    }
  },
  methods: {
    enableSearch() {
      this.loading = false;
      this.placeholder = '';
    },
    locate() {
      this.loading = true;
      navigator.geolocation.getCurrentPosition(this.autoLocateSuccess, this.autoLocateError);
    },
    handleSearch() {
      this.loading = true;
      this.codeAddress(this.search);
    },
    codeAddress(address) {
      const geocoder = new this.google.maps.Geocoder();
      geocoder.geocode({address: address}, this.addressLocateSuccess);
    },
    autoLocateSuccess(position) {
      this.enableSearch();
      this.lat = position.coords.latitude;
      this.long = position.coords.longitude;
      this.initializeMap();
      this.runQuery();
    },
    autoLocateError() {
      this.enableSearch();
      this.search = '';
      this.placeholder = t('Enter your City, State or ZIP');
    },
    addressLocateSuccess(results, status) {
      this.enableSearch();

      if (status === 'ZERO_RESULTS') {
        this.search = '';
        this.placeholder = t('Unable to locate, please try again.');
      } else {
        this.lat = results[0].geometry.location.lat();
        this.long = results[0].geometry.location.lng();
        this.initializeMap();
        this.runQuery();
      }
    },
    runQuery() {
      const locCurrent = new this.google.maps.LatLng(this.lat, this.long);
      const geocoder = new this.google.maps.Geocoder();
      geocoder.geocode({latLng: locCurrent}, this.geocode);
    },
    geocode(results) {
      if (results[1]) {
        let city = '';
        let zipCode = '';
        let country = '';

        for (let r = 0; r < 3; r++) {
          for (let i = 0; i < results[r].address_components.length; i++) {
            for (let b = 0; b < results[r].address_components[i].types.length; b++) {
              if (results[r].address_components[i].types[b] === 'locality') {
                city = results[r].address_components[i];
                break;
              }
            }
          }
        }

        for (let r = 0; r < 3; r++) {
          for (let i = 0; i < results[r].address_components.length; i++) {
            for (let b = 0; b < results[r].address_components[i].types.length; b++) {
              if (results[r].address_components[i].types[b] === 'postal_code') {
                zipCode = results[r].address_components[i];
                break;
              }
            }
          }
        }

        for (let r = 0; r < 3; r++) {
          for (let i = 0; i < results[r].address_components.length; i++) {
            for (let b = 0; b < results[r].address_components[i].types.length; b++) {
              if (results[r].address_components[i].types[b] === 'country') {
                country = results[r].address_components[i];
                break;
              }
            }
          }
        }

        this.zipCode = zipCode.short_name;
        this.countryCode = country.short_name;
        this.search = `${city.long_name}, ${zipCode.short_name}`;
        this.placeholder = '';
      }

      $.ajax({
        url: `/api/v1/supply-chain-filter?coordinates=[${this.lat},${this.long}]&zipCode=${this.zipCode}&countryCode=${this.countryCode}&_format=json`
      })
        .done(data => {
          this.supplyChainMapError = false;
          this.supplyChainMapLoading = false;
          this.supplyChainMapResults = data;
        })
        .catch(() => {
          this.supplyChainMapError = true;
          this.supplyChainMapLoading = false;
          this.supplyChainMapResults = false;
        });
    },
    initializeMap() {
      const mapDiv = document.getElementById('where-to-buy-map');
      if (mapDiv) {
        const styles = [
          {
            featureType: 'all',
            elementType: 'all',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'all',
            elementType: 'geometry',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'all',
            elementType: 'geometry.stroke',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'all',
            elementType: 'labels',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'all',
            elementType: 'labels.text',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'all',
            elementType: 'labels.icon',
            stylers: [{visibility: 'off'}]
          },
          {
            featureType: 'administrative',
            elementType: 'labels.text.fill',
            stylers: [
              {color: '#666666'},
              {saturation: '0'}
            ]
          },
          {
            featureType: 'landscape',
            elementType: 'all',
            stylers: [{color: '#f2f2f2'}]
          },
          {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [
              {visibility: 'simplified'},
              {color: '#f4f4f4'},
              {saturation: '0'}
            ]
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'all',
            stylers: [{visibility: 'off'}]
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry.fill',
            stylers: [{visibility: 'on'}]
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry.stroke',
            stylers: [{visibility: 'on'}]
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'labels.text',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'poi',
            elementType: 'all',
            stylers: [{visibility: 'off'}]
          },
          {
            featureType: 'poi',
            elementType: 'geometry',
            stylers: [
              {color: '#d5e9cd'},
              {saturation: '0'},
              {visibility: 'simplified'}
            ]
          },
          {
            featureType: 'poi',
            elementType: 'geometry.stroke',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'road',
            elementType: 'all',
            stylers: [{saturation: -100}, {lightness: 45}]
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'road.highway',
            elementType: 'all',
            stylers: [{visibility: 'simplified'}]
          },
          {
            featureType: 'road.arterial',
            elementType: 'labels.icon',
            stylers: [{visibility: 'off'}]
          },
          {
            featureType: 'transit',
            elementType: 'all',
            stylers: [{visibility: 'off'}]
          },
          {
            featureType: 'water',
            elementType: 'all',
            stylers: [
              {color: '#46bcec'},
              {visibility: 'on'}
            ]
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [
              {color: '#a7c7de'},
              {saturation: '0'}
            ]
          },
          {
            featureType: 'water',
            elementType: 'labels',
            stylers: [
              {color: '#f4f4f4'},
              {saturation: '0'},
              {visibility: 'simplified'}
            ]
          }
        ];

        const styledMap = new this.google.maps.StyledMapType(styles, {
          name: t('Sloan Local Reps & Distributors Map')
        });

        let defaultLatlng = new this.google.maps.LatLng(
          this.lat,
          this.long
        );

        let currentZoom;
        if (this.supplyChainMapResults) {
          currentZoom = 9;
        } else {
          currentZoom = 4;
        }

        const mapOptions = {
          center: defaultLatlng,
          zoom: currentZoom,
          scrollwheel: false
        };

        this.map = new this.google.maps.Map(
          mapDiv,
          mapOptions
        );
        this.map.mapTypes.set('map_style', styledMap);
        this.map.setMapTypeId('map_style');

        if (this.supplyChainMapResults) {
          const localReps = this.supplyChainMapResults.localReps;
          const localDistributors = this.supplyChainMapResults
            .localDistributors;

          for (let i = 0; i < localReps.length; i++) {
            const pinLatLng = new this.google.maps.LatLng(
              localReps[i].lat,
              localReps[i].long
            );

            const marker = new this.google.maps.Marker({
              position: pinLatLng,
              map: this.map,
              id: localReps[i].nid,
              title: localReps[i].title,
              icon: repPin
            });

            this.markers.push(marker);

            this.google.maps.event.addListener(
              marker,
              'click',
              () => {
                this.markerClick(marker);
              }
            );
          }

          for (let i = 0; i < localDistributors.length; i++) {
            const pinLatLng = new this.google.maps.LatLng(
              localDistributors[i].lat,
              localDistributors[i].long
            );

            const marker = new this.google.maps.Marker({
              position: pinLatLng,
              map: this.map,
              id: localDistributors[i].nid,
              title: localDistributors[i].title,
              icon: dealerPin
            });

            this.markers.push(marker);

            this.google.maps.event.addListener(
              marker,
              'click',
              () => {
                this.markerClick(marker);
              }
            );
          }
        }
      }
    },
    locateMarker(event) {
      const target = event.target;
      const targetRepDealer = target.closest('.rep-dealer');
      this.markerClick(targetRepDealer);
    },
    markerClick(mark)
    {
      $('.rep-dealer-list li').removeClass('active');

      const $repDealerList = $('.rep-dealer-list');

      const activeMarker = $repDealerList.find('#' + mark.id);
      activeMarker.addClass('active');

      $repDealerList.animate({
        scrollTop:
          $repDealerList.scrollTop() +
          activeMarker.position().top -
          activeMarker.outerHeight() * 2.5
      }, 500);

      for (let i = 0; i < this.markers.length; i++) {
        this.markers[i].setZIndex();

        if (this.markers[i].icon.fillColor === '#006bb6') {
          this.markers[i].setIcon(repPin);
        }
        if (this.markers[i].icon.fillColor === '#7ac254') {
          this.markers[i].setIcon(dealerPin);
        }

        if (this.markers[i].id === mark.id) {
          this.map.setZoom(12);
          this.map.panTo(this.markers[i].position);
          this.markers[i].setZIndex(
            this.google.maps.Marker.MAX_ZINDEX + 1
          );

          if (this.markers[i].icon.fillColor === '#006bb6') {
            this.markers[i].setIcon(repPinActive);
          }

          if (this.markers[i].icon.fillColor === '#7ac254') {
            this.markers[i].setIcon(dealerPinActive);
          }
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';
@import '../../scss/system';
@import '../../scss/mixins';

.rep-options-wrapper {
  display: flex;
  flex-direction: column-reverse;

  .rep-dealer-options {
    border-bottom: none;
  }

  .legend {
    border-bottom: none;
  }

  @media (min-width: $screen-md) {
    flex-direction: row-reverse;
    justify-content: space-between;
  }

  &.container {
    &:before, &:after {
      display: none;
    }
  }
}

.rep-dealer-options {
  padding: 16px 0;
  border-bottom: 1px solid $gray;

  .form-group {
    margin: 0;
  }

  .search {
    input {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;

      &:focus {
        border-color: $color-blue-400;
      }
    }

    button {
      height: 45px;
      border-radius: 0;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }

  .show-online {
    color: $gray-darker;

    &:hover,
    &.active {
      color: $text-color;
    }

    span {
      letter-spacing: 0.08em;
    }
  }
}

.rep-dealer-list-wrapper {
  border-right: 1px solid $color-gray-400;

  @media (min-width: $screen-md) {
    grid-column: 1 / 2;
  }

  .legend {
    display: none;
  }
}

.legend {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  padding: 16px;
  background-color: $color-white;
  border-bottom: 1px solid $color-gray-400;

  .reps {
    display: flex;
    align-items: center;
    gap: 4px;
    color: $color-blue-400;
  }

  .dealers {
    display: flex;
    align-items: center;
    gap: 4px;
    color: $color-green-400;
  }
}

.supply-chain-map {
  height: 100%;
  padding-block: 0;
}

.rep-dealer-viewer {
  display: grid;
  border-top: 1px solid $color-gray-400;
  border-bottom: 1px solid $color-gray-400;

  @media (min-width: $screen-md) {
    grid-template-columns: repeat(3, 1fr);
    max-width: 1440px;
    margin-inline: auto;
  }

  .contact-info {
    padding: 0 30px;
    text-align: left;
    color: $gray-darker;

    h3 {
      padding-top: 15px;
      text-transform: uppercase;
      font-weight: $medium;
    }
  }
}

.rep-dealer-list {
  margin-bottom: 0;
  padding-left: 0;
  list-style: none;
  height: calc(100vh - 70px - 50px - 120px);
  overflow: hidden;
  overflow-y: auto;
}

.rep-dealer {
  padding: 16px;
  background-color: $color-white;
  border-bottom: 1px solid $color-gray-400;
  text-align: left;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
  }

  &:hover,
  &.active {
    background-color: $gray-light;
  }

  &.rep .rep-dealer-name {
    color: $color-blue-400;
  }

  &.dealer .rep-dealer-name {
    color: $color-green-400;
  }
}

.rep-dealer-name {
  margin-top: 0;
  font-size: $font-size-h4;
  text-transform: uppercase;
  font-weight: $medium;

  i {
    padding-right: 5px;
  }
}

.rep-dealer-distance {
  color: $gray-dark;
}

.rep-dealer-address {
  margin-top: 10px;
}

.rep-dealer-map {
  order: -1;
  height: 100%;
  width: 100%;

  > div {
    height: 100%;
    background: $gray-light;
  }

  .rep-dealer-options {
    display: none;
  }

  @media (min-width: $screen-md) {
    grid-column: 2 / 4;
    order: initial;
  }
}

#where-to-buy-map {
  height: 100%;
}

// Slo Rep Locator Modal Styles

.slo-rep-locator {
  .rep-options-wrapper {
    display: none;
  }

  .rep-dealer-list-wrapper {
    border-right: none;

    .legend {
      position: sticky;
      top: 0;
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px;
      background-color: $color-white;
      border-bottom: 1px solid $color-gray-400;

      @media (min-width: $screen-md) {
        min-height: 78px;
      }

      .reps {
        color: $color-blue-400;
        font-weight: 500;
      }

      .dealers {
        color: $color-green-400;
        font-weight: 500;
      }
    }
  }

  .supply-chain-map {
    height: 448px;
  }

  .rep-dealer-viewer {
    display: grid;
    border-top: none;
    border-bottom: none;

    @media (min-width: $screen-md) {
      grid-template-columns: repeat(2, 1fr);
    }

    .contact-info {
      text-align: left;
    }
  }

  .rep-dealer-list {
    height: 397px;

    @media (min-width: $screen-md) {
      height: 462px;
      border-right: 1px solid $color-gray-400;
    }
  }

  .rep-dealer {
    text-align: left;
  }

  .rep-dealer-map {
    order: -1;
    height: 100%;
    width: 100%;

    > div {
      height: 100%;
      background: $gray-light;
    }

    .rep-dealer-options {
      display: block;
      padding: 16px;
    }

    @media (min-width: $screen-md) {
      order: initial;
    }
  }

  #where-to-buy-map {
    height: 462px;
  }
}
</style>
